import { trackEvent } from 'fathom-client'
import { MoreHorizontal, MoreVertical, Share, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Button } from '#app/components/ui/button'
import { trackSurfaceView, trackUIEvent } from '#app/utils/plausible.client.js'

const STORAGE_KEY = 'im-pwa-prompt-v1'

// Reveal the install prompt only AFTER the first assistant answer has
// finished streaming, plus a short reading window so the user can glance
// the result before we knock. Dispatched from chat-page.tsx as the custom
// event below.
export const FIRST_ANSWER_EVENT = 'im-first-answer-complete'
const POST_ANSWER_DELAY_MS = 5000

// localStorage helpers — Safari Private Browsing throws QuotaExceededError
// on setItem and (rarely) getItem, which would otherwise bubble out of the
// install effect and end up in Sentry. Treat any failure as "no flag set"
// so the install prompt degrades to its always-show baseline.
function storageGet(key: string): string | null {
	try {
		return localStorage.getItem(key)
	} catch {
		return null
	}
}
function storageSet(key: string, value: string): void {
	try {
		localStorage.setItem(key, value)
	} catch {
		/* Safari Private Browsing — silently swallow. */
	}
}

type Platform = 'android' | 'ios' | 'none'
type Browser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'samsung' | 'other'

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

interface Context {
	platform: Platform
	browser: Browser
}

function detectContext(): Context {
	if (typeof window === 'undefined') return { platform: 'none', browser: 'other' }
	const standalone =
		window.matchMedia('(display-mode: standalone)').matches ||
		(window.navigator as Navigator & { standalone?: boolean }).standalone ===
			true
	if (standalone) return { platform: 'none', browser: 'other' }
	const ua = window.navigator.userAgent

	// iPadOS 13+ reports as Mac; identify via touch points.
	// `maxTouchPoints` is part of the Pointer Events spec and present on all
	// modern Navigator instances (defaults to 0 on non-touch); the `?? 0`
	// covers any hypothetical absence.
	const isIPadOS =
		ua.includes('Macintosh') &&
		(window.navigator.maxTouchPoints ?? 0) > 1

	if (/iPad|iPhone|iPod/.test(ua) || isIPadOS) {
		// All iOS browsers run on WebKit but expose distinct UA tokens.
		if (/CriOS\//.test(ua)) return { platform: 'ios', browser: 'chrome' }
		if (/EdgiOS\//.test(ua)) return { platform: 'ios', browser: 'edge' }
		if (/FxiOS\//.test(ua)) return { platform: 'ios', browser: 'firefox' }
		if (/Safari\//.test(ua)) return { platform: 'ios', browser: 'safari' }
		return { platform: 'ios', browser: 'other' }
	}
	if (/Android/.test(ua)) {
		// SamsungBrowser must precede Chrome — its UA contains both tokens.
		if (/SamsungBrowser\//.test(ua)) return { platform: 'android', browser: 'samsung' }
		if (/EdgA\//.test(ua)) return { platform: 'android', browser: 'edge' }
		if (/Firefox\//.test(ua)) return { platform: 'android', browser: 'firefox' }
		if (/Chrome\//.test(ua)) return { platform: 'android', browser: 'chrome' }
		return { platform: 'android', browser: 'other' }
	}
	return { platform: 'none', browser: 'other' }
}

export function InstallPrompt() {
	const [open, setOpen] = useState(false)
	const [showInstructions, setShowInstructions] = useState(false)
	const [showPostInstall, setShowPostInstall] = useState(false)
	const [ctx, setCtx] = useState<Context>({
		platform: 'none',
		browser: 'other',
	})
	const [installEvent, setInstallEvent] =
		useState<BeforeInstallPromptEvent | null>(null)

	useEffect(() => {
		if (typeof window === 'undefined') return
		if (storageGet(STORAGE_KEY)) return
		const detected = detectContext()
		setCtx(detected)
		if (detected.platform === 'none') return

		const onBeforeInstallPrompt = (e: Event) => {
			e.preventDefault()
			setInstallEvent(e as BeforeInstallPromptEvent)
		}
		const onAppInstalled = () => {
			storageSet(STORAGE_KEY, 'installed')
			// On Android the icon lands in the app drawer rather than the home
			// screen and users often don't know where to find it; keep the sheet
			// open with a confirmation + guidance until they dismiss it.
			setShowPostInstall(true)
			setOpen(true)
			trackUIEvent({
				eventName: 'form_submit',
				label: 'pwa_installed',
				location: 'assistent_start_page',
				extraProps: { platform: detected.platform, browser: detected.browser },
			})
			// Fathom mirror — only for the converting event, to keep historical
			// install-count continuity until Plausible is validated.
			trackEvent('PWA Installed')
		}
		window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
		window.addEventListener('appinstalled', onAppInstalled)

		// Open the sheet only after the first answer has streamed in, plus
		// a brief reading window. Dispatched from chat-page.tsx.
		let revealTimer: ReturnType<typeof setTimeout> | undefined
		const onFirstAnswer = () => {
			if (revealTimer !== undefined) return
			revealTimer = setTimeout(() => setOpen(true), POST_ANSWER_DELAY_MS)
		}
		window.addEventListener(FIRST_ANSWER_EVENT, onFirstAnswer)

		return () => {
			if (revealTimer !== undefined) clearTimeout(revealTimer)
			window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
			window.removeEventListener('appinstalled', onAppInstalled)
			window.removeEventListener(FIRST_ANSWER_EVENT, onFirstAnswer)
		}
	}, [])

	// What does the entry-point button do for this combination?
	const canTriggerNativeDialog =
		ctx.platform === 'android' && installEvent !== null

	const dismiss = (step: 'entry' | 'instructions') => {
		trackUIEvent({
			eventName: 'button_click',
			label: 'pwa_prompt_dismissed',
			location: 'assistent_start_page',
			extraProps: { platform: ctx.platform, browser: ctx.browser, step },
		})
		storageSet(STORAGE_KEY, 'dismissed')
		setOpen(false)
	}

	// Closing the post-install confirmation is NOT a dismissal — the user
	// just installed the app, so firing `pwa_prompt_dismissed` here would
	// inflate the goal counter with successful installs. We also leave the
	// 'installed' marker in storage intact so we don't overwrite the
	// install signal.
	const closePostInstall = () => setOpen(false)

	const handleAndroidInstall = async () => {
		if (!installEvent) return
		try {
			await installEvent.prompt()
			const choice = await installEvent.userChoice
			if (choice.outcome === 'accepted') {
				trackUIEvent({
					eventName: 'button_click',
					label: 'pwa_native_accepted',
					location: 'assistent_start_page',
					extraProps: { browser: ctx.browser },
				})
				// Wait for the appinstalled event to fire so we can show the
				// post-install confirmation. Don't close the sheet yet.
				return
			}
			trackUIEvent({
				eventName: 'button_click',
				label: 'pwa_native_dismissed',
				location: 'assistent_start_page',
				extraProps: { browser: ctx.browser },
			})
			storageSet(STORAGE_KEY, 'dismissed')
			setOpen(false)
		} catch (err) {
			// `prompt()` / `userChoice` threw (extension intercept, double-tap
			// user-gesture loss, OEM browser quirk). We can't recover with manual
			// instructions on Chrome — the AndroidFirefoxInstructions panel below
			// is Firefox-specific copy ("Tryck på menyknappen i Firefox"), so
			// routing Chrome here would just confuse the user. Clear the install
			// event so the entry sheet's showEntry computation falls through to
			// `return false // unknown Android browser` and the sheet hides; the
			// user's natural fallback is Chrome's address-bar install affordance.
			//
			// Surface the failure so we know if it happens at scale: console.warn
			// reaches Sentry via captureConsoleIntegration, and pwa_native_error
			// gives us a Plausible-side count to compare against pwa_prompt_primary
			// (with action: native_dialog) for drop-off analysis.
			console.warn('PWA prompt() failed', err)
			trackUIEvent({
				eventName: 'button_click',
				label: 'pwa_native_error',
				location: 'assistent_start_page',
				extraProps: { browser: ctx.browser },
			})
			setInstallEvent(null)
		}
	}

	const onPrimary = () => {
		trackUIEvent({
			eventName: 'button_click',
			label: 'pwa_prompt_primary',
			location: 'assistent_start_page',
			extraProps: {
				platform: ctx.platform,
				browser: ctx.browser,
				action: canTriggerNativeDialog ? 'native_dialog' : 'show_instructions',
			},
		})
		if (canTriggerNativeDialog) {
			void handleAndroidInstall()
		} else {
			setShowInstructions(true)
		}
	}

	// Decide whether to show anything at all.
	const showEntry = (() => {
		if (!open) return false
		if (showPostInstall) return true
		if (ctx.platform === 'ios') return true // every iOS browser gets manual instructions
		if (ctx.platform === 'android') {
			if (canTriggerNativeDialog) return true // Chrome / Edge / Samsung
			if (ctx.browser === 'firefox') return true // Firefox = manual instructions
			return false // unknown Android browser, hide
		}
		return false
	})()

	// Fire surface_view exactly once when the initial sheet becomes visible.
	// Not fired for the post-install confirmation — that's a separate surface
	// the user already paid for with an install.
	const entrySheetVisible = showEntry && !showPostInstall
	useEffect(() => {
		if (!entrySheetVisible) return
		trackSurfaceView({
			surface: 'pwa_install_sheet',
			extraProps: { platform: ctx.platform, browser: ctx.browser },
		})
	}, [entrySheetVisible, ctx.platform, ctx.browser])

	if (!showEntry) return null

	return (
		<div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md duration-300 animate-in fade-in slide-in-from-bottom-4 md:hidden">
			<div className="overflow-hidden rounded-2xl border border-border bg-background shadow-lg">
				{showPostInstall ? (
					<PostInstallConfirmation onClose={closePostInstall} />
				) : showInstructions ? (
					ctx.platform === 'ios' ? (
						<IosInstructions
							browser={ctx.browser}
							onClose={() => dismiss('instructions')}
						/>
					) : (
						<AndroidFirefoxInstructions
							onClose={() => dismiss('instructions')}
						/>
					)
				) : (
					<div className="flex items-start gap-3 p-4">
						<div className="flex-1">
							<h3 className="text-sm font-semibold">
								Lägg till på hemskärmen
							</h3>
							<p className="mt-1 text-xs text-muted-foreground">
								Snabb åtkomst till assistenten direkt från hemskärmen.
							</p>
							<div className="mt-3 flex gap-2">
								<Button size="sm" onClick={onPrimary}>
									{canTriggerNativeDialog ? 'Lägg till' : 'Visa hur'}
								</Button>
								<Button
									size="sm"
									variant="ghost"
									onClick={() => dismiss('entry')}
								>
									Inte nu
								</Button>
							</div>
						</div>
						<button
							type="button"
							onClick={() => dismiss('entry')}
							className="text-muted-foreground transition-colors hover:text-foreground"
							aria-label="Stäng"
						>
							<X className="size-4" />
						</button>
					</div>
				)}
			</div>
		</div>
	)
}

function PostInstallConfirmation({ onClose }: { onClose: () => void }) {
	return (
		<div className="p-4">
			<div className="mb-2 flex items-center justify-between">
				<h3 className="text-sm font-semibold">Klart - appen är installerad</h3>
				<button
					type="button"
					onClick={onClose}
					className="text-muted-foreground transition-colors hover:text-foreground"
					aria-label="Stäng"
				>
					<X className="size-4" />
				</button>
			</div>
			<p className="text-sm text-foreground">
				Du hittar Internetmedicin bland dina appar - sök efter den om den inte
				syns direkt.
			</p>
			<p className="mt-2 text-sm text-muted-foreground">
				Vill du ha den på hemskärmen? Tryck och håll på ikonen några sekunder,
				välj sedan att lägga till den på hemskärmen.
			</p>
		</div>
	)
}

function ShareBadge() {
	return (
		<span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs font-medium">
			<Share className="size-3.5" /> Dela
		</span>
	)
}

function IconBadge({
	icon: Icon,
	label,
}: {
	icon: typeof MoreHorizontal
	label: string
}) {
	return (
		<span
			className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs font-medium"
			aria-label={label}
		>
			<Icon className="size-3.5" />
		</span>
	)
}

const IOS_FIRST_STEP: Record<Browser, ReactNode> = {
	safari: (
		<>
			<span>Tryck på</span> <ShareBadge />{' '}
			<span>i Safari (längst ner i toolbaren, eller bakom</span>{' '}
			<IconBadge icon={MoreHorizontal} label="Meny" />{' '}
			<span>vid adressfältet i iOS 26).</span>
		</>
	),
	chrome: (
		<>
			<span>Tryck på</span> <ShareBadge />{' '}
			<span>uppe till höger i adressfältet.</span>
		</>
	),
	edge: (
		<>
			<span>Tryck på menyknappen</span>{' '}
			<IconBadge icon={MoreHorizontal} label="Meny" />{' '}
			<span>i nedre verktygsfältet, välj sedan</span> <ShareBadge />.
		</>
	),
	firefox: (
		<>
			<span>Tryck på</span> <ShareBadge />{' '}
			<span>i adressfältet i Firefox.</span>
		</>
	),
	samsung: (
		<>
			<span>Tryck på</span> <ShareBadge />{' '}
			<span>i adressfältet.</span>
		</>
	),
	other: (
		<>
			<span>Tryck på</span> <ShareBadge />{' '}
			<span>i din webbläsare.</span>
		</>
	),
}

function IosInstructions({
	browser,
	onClose,
}: {
	browser: Browser
	onClose: () => void
}) {
	return (
		<div className="p-4">
			<div className="mb-3 flex items-center justify-between">
				<h3 className="text-sm font-semibold">Lägg till på hemskärmen</h3>
				<button
					type="button"
					onClick={onClose}
					className="text-muted-foreground transition-colors hover:text-foreground"
					aria-label="Stäng"
				>
					<X className="size-4" />
				</button>
			</div>
			<ol className="space-y-2 text-sm text-foreground">
				<li className="flex flex-wrap items-center gap-1.5">
					<span className="text-muted-foreground">1.</span>
					{IOS_FIRST_STEP[browser]}
				</li>
				<li>
					<span className="text-muted-foreground">2.</span> Scrolla ner och
					välj <strong>"Lägg till på hemskärmen"</strong>.
				</li>
				<li>
					<span className="text-muted-foreground">3.</span> Tryck{' '}
					<strong>"Lägg till"</strong> uppe till höger.
				</li>
			</ol>
		</div>
	)
}

function AndroidFirefoxInstructions({ onClose }: { onClose: () => void }) {
	return (
		<div className="p-4">
			<div className="mb-3 flex items-center justify-between">
				<h3 className="text-sm font-semibold">Lägg till på hemskärmen</h3>
				<button
					type="button"
					onClick={onClose}
					className="text-muted-foreground transition-colors hover:text-foreground"
					aria-label="Stäng"
				>
					<X className="size-4" />
				</button>
			</div>
			<ol className="space-y-2 text-sm text-foreground">
				<li className="flex flex-wrap items-center gap-1.5">
					<span className="text-muted-foreground">1.</span>
					<span>Tryck på menyknappen</span>{' '}
					<IconBadge icon={MoreVertical} label="Meny" />{' '}
					<span>i Firefox.</span>
				</li>
				<li>
					<span className="text-muted-foreground">2.</span> Välj{' '}
					<strong>"Installera"</strong> eller{' '}
					<strong>"Lägg till på hemskärmen"</strong>.
				</li>
			</ol>
		</div>
	)
}
